/**
 * Пары (G15 «Память»): карточки рубашкой вверх в сетке 3 колонки; тап
 * открывает, вторая — сверяется. Пара остаётся открытой (`done`), не пара
 * закрывается на следующем тапе. Все пары найдены — исход сразу; кнопка
 * «Закончить» отдаёт найденное. Сравнение — по `pair` карточки.
 */
import { t } from '@life/i18n';
import { useState } from 'preact/hooks';

export interface PairCard {
  pair: number;
  text: string;
  /** Подпись в моно (год). */
  meta?: string;
}

export interface PairsProps {
  label: string;
  cards: PairCard[];
  pairs: number;
  /** «Карточки плывут» (деменция): класс `drift`. */
  drift?: boolean;
  onDone: (found: [number, number][]) => void;
}

export function Pairs({ label, cards, pairs, drift = false, onDone }: PairsProps) {
  const [open, setOpen] = useState<number[]>([]);
  const [found, setFound] = useState<[number, number][]>([]);
  const doneIdx = new Set(found.flat());

  const tap = (i: number) => {
    if (doneIdx.has(i) || open.includes(i)) return;
    if (open.length >= 2) {
      setOpen([i]);
      return;
    }
    if (open.length === 1) {
      const a = open[0] as number;
      if (cards[a]?.pair === cards[i]?.pair) {
        const next: [number, number][] = [...found, [a, i]];
        setFound(next);
        setOpen([]);
        if (next.length >= pairs) onDone(next);
        return;
      }
    }
    setOpen([...open, i]);
  };

  return (
    <div class="pairs-wrap">
      <p class="hint mono" aria-live="polite">
        {t('ui.minigame.pairs.found', { n: found.length, total: pairs })}
      </p>
      <div class={`pairs${drift ? ' drift' : ''}`} role="group" aria-label={label}>
        {cards.map((c, i) => {
          const isDone = doneIdx.has(i);
          const isOpen = open.includes(i);
          return (
            <button
              type="button"
              key={i}
              class={`card${isOpen ? ' open' : ''}${isDone ? ' done' : ''}${!isOpen && !isDone ? ' back' : ''}`}
              aria-label={t('ui.minigame.pairs.open', { n: i + 1 })}
              aria-pressed={isOpen || isDone}
              disabled={isDone}
              onClick={() => tap(i)}
            >
              {isOpen || isDone ? (
                <span class="card-text">
                  {c.meta && <span class="mono note">{c.meta}</span>}
                  {c.text}
                </span>
              ) : (
                <span class="mono">{i + 1}</span>
              )}
            </button>
          );
        })}
      </div>
      <button type="button" class="btn ghost" onClick={() => onDone(found)}>
        {t('ui.minigame.pairs.done')}
      </button>
    </div>
  );
}
